import { observer } from 'mobx-react-lite';
import { FC, useState } from 'react';
import { ModelUserProfile } from '~/models/model.user.profile';
import { IAppSelection } from '~/views/types';

const APPS = [
    {
        code: 'JP',
        name: 'Hard Rock Jackpot Planet',
        shortName: 'Jackpot Planet',
        icon: '/assets/img/select-app-jp.png',
        alt: 'Jackpot Planet',
    },
    {
        code: 'NC',
        name: 'Hard Rock Neverland Casino',
        shortName: 'Neverland Casino',
        icon: '/assets/img/select-app-nc.png',
        alt: 'Neverland Casino',
    },
];

export const AppSelection: FC<IAppSelection> = observer(
    ({ onChooseProfile, getUserProfiles, alertDialogSrv, showLoading }) => {
        const profileByCode = new Map<string, ModelUserProfile>();
        (getUserProfiles?.() ?? []).forEach(profile => profileByCode.set(profile.appCode, profile));

        const linkedProfiles = APPS.flatMap(app => {
            const profile = profileByCode.get(app.code);
            return profile ? [profile] : [];
        });
        const unlinkedApp = APPS.filter(app => !profileByCode.has(app.code));
        const onlyLinkedUid = linkedProfiles.length === 1 ? linkedProfiles[0].uid : null;

        const [selectedUid, setSelectedUid] = useState<string | null>(onlyLinkedUid);

        const handleSelect = (uid: string) => {
            setSelectedUid(prev => (prev === uid ? null : uid));
        };

        const handleContinue = () => {
            if (selectedUid) onChooseProfile?.(selectedUid, alertDialogSrv);
        };

        const formatWD = (wd: string | number | undefined) => `$${Number(wd ?? 0).toLocaleString()}`;

        return (
            <>
                <h3 className="app-selection-header">Select a Game</h3>
                <p className="app-selection-text">Which account would you like to purchase for?</p>

                <div className="app-select-grid">
                    {APPS.map(app => {
                        const profile = profileByCode.get(app.code);
                        const isSelected = Boolean(profile && selectedUid === profile.uid);

                        if (!profile) {
                            return (
                                <div key={app.code} className="app-card unlinked">
                                    <div className="app-card-left">
                                        <div className="app-icon-wrap">
                                            <img className="app-icon" src={app.icon} alt={app.alt} />
                                        </div>
                                        <div className="app-meta">
                                            <div className="app-name">{app.name}</div>
                                            <div className="app-unlinked">Not linked to this phone number</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <button
                                key={app.code}
                                type="button"
                                className={`app-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleSelect(profile.uid)}
                                aria-pressed={isSelected}
                                disabled={showLoading}
                            >
                                <div className="app-card-left">
                                    <div className="app-icon-wrap">
                                        <img className="app-icon" src={app.icon} alt={app.alt} />
                                        <img
                                            className="app-check"
                                            src="/assets/img/select-app-green-check.png"
                                            alt=""
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div className="app-meta">
                                        <div className="app-name">{app.name}</div>
                                        <div className="app-user">{profile.displayName}</div>
                                        <div className="app-balance-chip">
                                            <img
                                                className="wd-icon"
                                                src="/assets/img/letter-w-user-menu.svg"
                                                alt=""
                                                aria-hidden="true"
                                            />
                                            <span className="wd-label">WDOLLARS</span>
                                            <span className="wd-value">{formatWD(profile.wdollarBalance)}</span>
                                        </div>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
                {unlinkedApp.length === 1 && (
                    <p className="app-unlinked-note">
                        {unlinkedApp[0].shortName} isn&apos;t linked to this phone number. Open the game and check the
                        phone number linked to your account.
                    </p>
                )}
                <div className="app-select-btn-wrap">
                    <button
                        type="button"
                        className="btn continue-btn"
                        disabled={!selectedUid || showLoading}
                        onClick={handleContinue}
                    >
                        Continue
                    </button>
                </div>
            </>
        );
    },
);
